import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardFooter, Col, Row, Spinner } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { toZonedTime } from 'date-fns-tz';
import * as Yup from 'yup';

import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';

import {
  archiveOptionsGet,
  dialerCallConnectivityReportGet,
  downloadDialerCallConnectivityReport,
  getListsByProcess,
  processAllOptionGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  checkModulePermission,
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';
import { useTimezone } from '../../../store/slices/commonSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import useReportDownload from '../../../hooks/useReportDownload';

// Dialer counterpart of the Voice Broadcast Call Connectivity Report: same filters, same
// Connected / Not Connected split, same click-a-count-to-download flow.

// Raw hangup cause -> display label. Raw values sharing a label have their counts merged.
const DISPOSITION_LABEL_MAP = {
  NORMAL_CLEARING: 'SUCCESS',
  AUTOORIGINATE: 'SUCCESS'
};

// Labels shown in the "Connected" table; everything else is "Not Connected".
const CONNECTED_LABELS = ['SUCCESS'];

const ALL_LIST_OPTION = { label: 'All', value: 'All' };

const getRawCause = (item) => item?.hangup_cause ?? item?.disposition ?? 'UNKNOWN';

// Remap labels and merge counts, keeping the raw causes for the download filter.
const buildReportRows = (rows = []) => {
  const merged = (Array.isArray(rows) ? rows : []).reduce((acc, item) => {
    const raw = getRawCause(item);
    const label = DISPOSITION_LABEL_MAP[raw] || raw;
    if (!acc[label]) acc[label] = { label, count: 0, rawCauses: [] };
    acc[label].count += Number(item?.count) || 0;
    if (!acc[label].rawCauses.includes(raw)) acc[label].rawCauses.push(raw);
    return acc;
  }, {});
  return Object.values(merged).sort((a, b) => b.count - a.count);
};

const sumCounts = (rows = []) => rows.reduce((acc, val) => acc + (Number(val?.count) || 0), 0);

const DialerCallConnectivityReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const userRolePermission = useUserRolePermission();
  const { isDownloading, downloadFile } = useReportDownload();
  // Without export permission the counts stay visible but are no longer download links.
  const canExport = checkModulePermission(
    'is_export',
    'Dialer Call Connectivity Report',
    userRolePermission
  );
  const countClassName = canExport
    ? 'text-center text-decoration-underline cursor-pointer'
    : 'text-center';
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [processOptions, setProcessOptions] = useState([]);
  // Starts true: the first search waits for the archive options, so the empty state
  // would otherwise flash before the loader.
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState({ connected: [], notConnected: [] });
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [listOptions, setListOptions] = useState([ALL_LIST_OPTION]);
  const hasAutoSearchedRef = useRef(false);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    processId: '',
    listId: 'All',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
  });

  // Same params the Voice Broadcast report sends; `range` lets the first automatic search
  // use the month range before setFieldValue has landed in `values`.
  const getPayload = (range) => ({
    ...values,
    processId: values?.processId || 'All',
    listId: values?.listId || 'All',
    start_date: formatPayloadDate(range?.start_date || values.start_date),
    end_date: formatPayloadDate(range?.end_date || values.end_date)
  });

  const fetchReport = async (range) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const response = await dispatch(dialerCallConnectivityReportGet(getPayload(range))).unwrap();
      if (response) {
        const allRows = buildReportRows(response?.data);
        setReportData({
          connected: allRows.filter((row) => CONNECTED_LABELS.includes(row.label)),
          notConnected: allRows.filter((row) => !CONNECTED_LABELS.includes(row.label))
        });
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = () => fetchReport();

  // Runs the load-time search exactly once, whichever path resolves the range first.
  const runInitialSearch = (range) => {
    if (hasAutoSearchedRef.current) return;
    hasAutoSearchedRef.current = true;
    fetchReport(range);
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  // `/process` has no dialer flag, so drop the Voice Broadcast processes here — those
  // belong to the Voice Broadcast Call Connectivity Report.
  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      const options = (response?.data || [])
        .filter((val) => val.type !== 'VoiceBroadcast')
        .map((val) => ({ label: val.name, value: val.id }));
      setProcessOptions(options);
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getListOption = async (id) => {
    try {
      const response = await dispatch(getListsByProcess({ process_id: id })).unwrap();
      const options =
        response?.data?.map((val) => ({ label: val.list_name, value: val.id })) || [];
      setListOptions([ALL_LIST_OPTION, ...options]);
    } catch (error) {
      setListOptions([ALL_LIST_OPTION]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        setArchiveOptions(formatArchiveOptions(response.data, timezone));
      } else {
        setArchiveOptions([]);
        // No month options means no derived range, so fall back to the default dates.
        runInitialSearch();
      }
    } catch (error) {
      setArchiveOptions([]);
      runInitialSearch();
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getProcessOption();
  }, []);

  // Lists belong to a process. Reset to All on every process change, or a list id from the
  // previous process stays in the payload and filters the report to nothing.
  useEffect(() => {
    if (values.processId) {
      getListOption(values.processId);
    } else {
      setListOptions([ALL_LIST_OPTION]);
    }
    setFieldValue('listId', 'All');
  }, [values.processId]);

  // A row sends its RAW cause(s), comma-separated when merged; a Total sends 'All'.
  // `name` is the row label (or "<table> Total"), which also keys the cell's spinner.
  const onDownload = (name, count, hangupCause) => {
    if (!canExport) return;
    const payload = {
      ...getPayload(),
      hangupCause: Array.isArray(hangupCause) ? hangupCause.join(',') : hangupCause
    };
    const { start_date, end_date } = payload;
    downloadFile({
      name,
      count,
      fileName: `dialer_connectivity_report_${start_date}_to_${end_date}.xlsx`,
      request: (onDownloadProgress) =>
        dispatch(downloadDialerCallConnectivityReport({ ...payload, onDownloadProgress })).unwrap()
    });
  };

  const renderSpinner = (name) =>
    isDownloading(name) && <Spinner size="sm" color="primary" className="ms-1" />;

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const { minDate, maxDate } = useMemo(
    () => getDateRangeFromLabel(selectedOption?.label || null, timezone),
    [selectedOption?.label, timezone]
  );

  // The archive options land after mount. Like Call History, the report opens on today:
  // the first pass keeps the default today range and only fires the initial search, and
  // later month changes still snap the filter to the selected month's range.
  useEffect(() => {
    if (minDate && maxDate) {
      if (!hasAutoSearchedRef.current) {
        runInitialSearch();
        return;
      }
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
    } else if (archiveOptions?.length > 0) {
      runInitialSearch();
    }
  }, [minDate?.getTime(), maxDate?.getTime(), archiveOptions?.length]);

  const bodyHeight = isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)';

  const renderCauseTable = (title, rows) => (
    <table className="report-list-table">
      <thead>
        <tr className="title">
          <th colSpan={2}>{title}</th>
        </tr>
        <tr>
          <th>Disposition</th>
          <th style={{ width: '90px' }} className="text-center">
            Count
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((val) => (
          <tr key={val?.label}>
            <td>{val?.label}</td>
            <td>
              <div
                className={countClassName}
                onClick={() => onDownload(val?.label, val?.count, val?.rawCauses)}>
                {val?.count}
                {renderSpinner(val?.label)}
              </div>
            </td>
          </tr>
        ))}
        <tr>
          <td>
            <strong>Total</strong>
          </td>
          <td>
            <div
              className={countClassName}
              onClick={() => onDownload(`${title} Total`, sumCounts(rows), 'All')}>
              {sumCounts(rows)}
              {renderSpinner(`${title} Total`)}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <div>
      <AdminHeader
        title="Dialer Call Connectivity Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isRefresh
        onRefreshClick={() => onSubmit()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processId"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="List"
                  placeholder="Select List"
                  value={values.listId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="listId"
                  options={listOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: bodyHeight }}>
          <CallexLoader />
        </Card>
      ) : reportData.connected.length > 0 || reportData.notConnected.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div className="daily-report-table" style={{ height: bodyHeight }}>
            {reportData.connected.length > 0 &&
              renderCauseTable('Connected', reportData.connected)}
            {reportData.notConnected.length > 0 &&
              renderCauseTable('Not Connected', reportData.notConnected)}
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: bodyHeight }}>
          No data available
        </Card>
      )}
    </div>
  );
};

export default DialerCallConnectivityReport;
